import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { createQuinnRuntime, useQuinnApi, useQuinnChatSession } from "./hooks";
import {
    ALL_CLIENTS_STORED_VALUE,
    DEFAULT_SCOPE,
    clampPanelWidth,
    describeRoute,
    isQuinnFullscreenPath,
    QUINN_DEFAULT_WIDTH,
    quinnFullscreenPath,
    readPanelPrefs,
    writePanelPrefs,
    writeSelectedClientId,
} from "./utils";
import {
    QuinnContextValue,
    QuinnFullscreenExit,
    QuinnPageContext,
    QuinnScope,
    QuinnScopeMode,
    QUINN_OVERLAY_BREAKPOINT_PX,
} from "./types";
import { getCurrentBusiness } from "../../utils/OrgManagement.ts";
import { isDocumentDeepLink } from "../../lib/documentDeepLink.ts";

const QuinnContext = createContext<QuinnContextValue | null>(null);

export function useQuinn(): QuinnContextValue | null {
    return useContext(QuinnContext);
}

export function useRequiredQuinn(): QuinnContextValue {
    const value = useContext(QuinnContext);
    if (!value) {
        throw new Error("useRequiredQuinn must be used inside QuinnProvider");
    }
    return value;
}

export function useQuinnFullscreenView(): QuinnContextValue {
    const quinn = useRequiredQuinn();
    const { setFullscreenMounted } = quinn;
    useEffect(() => {
        setFullscreenMounted(true);
        return () => setFullscreenMounted(false);
    }, [setFullscreenMounted]);
    return quinn;
}

export function QuinnProvider({
    userId,
    canAskAllClients,
    children,
}: {
    userId: string;
    canAskAllClients: boolean;
    children: ReactNode;
}) {
    const location = useLocation();
    const navigate = useNavigate();
    const initialPrefs = useMemo(() => readPanelPrefs(userId), [userId]);
    const [openedFromSource] = useState(() => isDocumentDeepLink(location.search));

    const [isOpen, setIsOpen] = useState(initialPrefs.isOpen && !openedFromSource);
    const [width, setWidthState] = useState(initialPrefs.width);
    const [isOverlay, setIsOverlay] = useState(() => window.innerWidth < QUINN_OVERLAY_BREAKPOINT_PX);
    const [scope, setScopeState] = useState<QuinnScope>(DEFAULT_SCOPE);
    const [isFullscreenMounted, setFullscreenMounted] = useState(false);
    const isFullscreen = isFullscreenMounted || isQuinnFullscreenPath(location.pathname);
    const [draft, setDraft] = useState("");
    const fullscreenOriginRef = useRef<string | null>(null);

    const api = useQuinnApi();
    const runtime = useMemo(() => createQuinnRuntime(api), [api]);

    const route = useMemo(() => describeRoute(location.pathname), [location.pathname]);
    const pageContext = useMemo<QuinnPageContext>(
        () => ({
            path: location.pathname,
            label: route.label,
            businessId: getCurrentBusiness(),
            businessName: null,
        }),
        [location.pathname, route.label],
    );

    const {
        messages,
        chatId,
        chats,
        businesses,
        isBusy,
        isHydrating,
        send,
        stop,
        retry,
        startNewChat,
        openChat,
        answerPrompt,
        rateAnswer,
    } = useQuinnChatSession({
        runtime,
        isVisible: isOpen || isFullscreen,
        scope,
        pageContext,
        setScopeState,
        userId,
    });

    useEffect(() => {
        if (openedFromSource && !isOpen && width === initialPrefs.width) return;
        writePanelPrefs(userId, { isOpen, width });
    }, [userId, isOpen, width, openedFromSource, initialPrefs.width]);

    useEffect(() => {
        if (scope.mode === QuinnScopeMode.AllClients) {
            writeSelectedClientId(userId, ALL_CLIENTS_STORED_VALUE);
            return;
        }
        if (scope.businessId) writeSelectedClientId(userId, scope.businessId);
    }, [userId, scope.mode, scope.businessId]);

    useEffect(() => {
        function onResize() {
            setIsOverlay(window.innerWidth < QUINN_OVERLAY_BREAKPOINT_PX);
        }
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const setScope = useCallback((patch: Partial<QuinnScope>) => {
        setScopeState((current) => ({ ...current, ...patch }));
    }, []);

    const setWidth = useCallback((next: number) => {
        setWidthState(clampPanelWidth(next));
    }, []);

    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);
    const toggle = useCallback(() => setIsOpen((current) => !current), []);

    const enterFullscreen = useCallback(
        (artifactId?: string) => {
            if (!isQuinnFullscreenPath(location.pathname)) {
                fullscreenOriginRef.current = `${location.pathname}${location.search}`;
            }
            navigate(quinnFullscreenPath(chatId), artifactId ? { state: { openArtifactId: artifactId } } : undefined);
        },
        [chatId, location.pathname, location.search, navigate],
    );

    useEffect(() => {
        if (!isQuinnFullscreenPath(location.pathname)) fullscreenOriginRef.current = null;
    }, [location.pathname]);

    const exitFullscreen = useCallback(
        (target: QuinnFullscreenExit) => {
            const origin = fullscreenOriginRef.current ?? "/";
            fullscreenOriginRef.current = null;
            setIsOpen(target === "drawer");
            navigate(origin);
        },
        [navigate],
    );

    const value: QuinnContextValue = {
        isOpen,
        width: isOverlay ? Math.min(width, QUINN_DEFAULT_WIDTH) : width,
        isOverlay,
        isFullscreen,
        draft,
        messages,
        chatId,
        chats,
        businesses,
        scope,
        isBusy,
        isHydrating,
        canAskAllClients,
        pageContext,
        suggestions: route.suggestions,
        open,
        close,
        toggle,
        setWidth,
        setDraft,
        enterFullscreen,
        exitFullscreen,
        setFullscreenMounted,
        send,
        stop,
        retry,
        startNewChat,
        openChat,
        setScope,
        answerPrompt,
        rateAnswer,
    };

    return <QuinnContext.Provider value={value}>{children}</QuinnContext.Provider>;
}
